import React, { useState } from "react";
import { View, FlatList, Pressable, StyleSheet, ActivityIndicator, ScrollView } from "react-native";
import { ListTodo } from "lucide-react";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { useSearchTodosQuery } from "@axio/graphql/generated/graphql";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

export interface Todo {
  id: string;
  name: string;
  description?: string;
  date?: string | number | Date;
  completed?: boolean;
}

interface AddTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectTask: (task: Todo) => void;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({ visible, onClose, onSelectTask }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data, loading } = useSearchTodosQuery({
    variables: {
      searchQuery: searchQuery.trim() || undefined,
    },
    fetchPolicy: "network-only",
  });

  const todos = data?.searchTodosForUser?.data || [];

  const handleSelect = (todo: Todo) => {
    onSelectTask(todo);
    setSearchQuery("");
    onClose();
  };

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const renderTodoItem = ({ item }: { item: Todo }) => {
    const isHovered = hoveredId === item.id;
    return (
      <Pressable
        style={[styles.todoItem, isHovered && styles.todoItemHovered]}
        onPress={() => handleSelect(item)}
        onHoverIn={() => setHoveredId(item.id)}
        onHoverOut={() => setHoveredId(null)}>
        <View style={styles.todoIcon}>
          <ListTodo size={20} color={colors.grey[500]} />
        </View>
        <View style={styles.todoContent}>
          <CustomText text={item.name} useTranslationText={false} style={styles.todoName} numberOfLines={1} />
          {item.date && (
            <CustomText
              text={`Due ${new Date(item.date).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}`}
              useTranslationText={false}
              style={styles.todoDate}
            />
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <CustomModal visible={visible} onRequestClose={onClose} title="Add Task" showCloseButton ariaLabel="Add Task Modal">
      <View style={styles.modalContent}>
        <CustomText
          text="Select a task from your to-do list to attach to your message"
          useTranslationText={false}
          style={styles.subtitle}
        />

        <CustomTextInput
          inputType="search"
          placeholder="Search tasks..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.searchInput}
        />

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.brand[500]} />
          </View>
        ) : todos.length === 0 ? (
          <View style={styles.emptyContainer}>
            <CustomText
              text={searchQuery ? "No tasks found" : "No tasks due soon"}
              useTranslationText={false}
              style={styles.emptyText}
            />
          </View>
        ) : (
          <ScrollView style={styles.todoList} contentContainerStyle={styles.todoListContent} tabIndex={0}>
            {todos.map((item) => (
              <View key={item.id}>{renderTodoItem({ item })}</View>
            ))}
          </ScrollView>
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: "100%",
    maxHeight: 600,
  },
  subtitle: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[500],
    marginBottom: spacing.md,
  },
  searchInput: {
    marginBottom: spacing.sm,
    // @ts-expect-error - outlineOffset is a valid web CSS property for React Native Web
    outlineOffset: 0,
  },
  todoList: {
    maxHeight: 400,
  },
  todoListContent: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxs,
  },
  todoItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    marginBottom: spacing.xs,
    cursor: "pointer",
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  todoItemHovered: {
    backgroundColor: colors.brand[50],
    borderColor: colors.brand[300],
  },
  todoIcon: {
    marginRight: 12,
  },
  todoContent: {
    flex: 1,
  },
  todoName: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
    fontWeight: "500",
    marginBottom: 2,
  },
  todoDate: {
    fontSize: typographyScale.textXs.fontSize,
    color: colors.grey[500],
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[500],
  },
});
